import axios from "axios";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Slide from "@mui/material/Slide";
import { useCookies } from "react-cookie";
import { getCountries } from "../api/helpers";
import { completeProfile } from "../api/user";
import { isEmpty } from "../helpers/functions";
import * as routes from "../data/links/routes";
import { useAuth } from "../context/AuthContext";
import { errorTexts } from "../data/assets/texts";
import { GPLogo } from "../components/theme/logos";
import useScreenSize from "../context/ScreenContext";
import TextInput from "../components/Inputs/TextInput";
import DateInput from "../components/Inputs/DateInput";
import { Navigate, useNavigate } from "react-router-dom";
// import RadioInput from "../components/Inputs/RadioInput";
import SelectInput from "../components/Inputs/SelectInput";
import RequestButton from "../components/buttons/RequestButton";
import { useNotifications } from "../context/NotificationContext";
import { genderOptions, LEVELOPTIONS } from "../data/assets/arrays";
import { useState, useEffect, lazy, Suspense } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  MainTitle,
  ErrorText,
  MainText,
  AllCenteredColumn,
  SpacedRow,
  CenteredRow,
  FormButton,
  SecondaryTitle,
} from "../data/style/Style";
import { Theme } from "../data/style/Theme";
import ToggleButton from "../components/buttons/ToggleButton";

const LevelGuideModal = lazy(
  () => import("../components/Modals/LevelGuideModal"),
);

const TOTAL_STEPS = 3;
const STEP_TRANSITION_MS = 1000;

// Progress dots for the 3-step tutorial: the active step is a wide pill,
// the rest are small dots (filled once passed, dim otherwise).
const StepIndicator = ({ step }) => (
  <CenteredRow gap={1} justifyContent={"center"}>
    {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((n) => (
      <Box
        key={n}
        sx={{
          height: "6px",
          width: n === step ? "28px" : "6px",
          borderRadius: "999px",
          background: n <= step ? Theme.palette.orange : Theme.palette.dim,
          transition: "all .3s ease",
        }}
      />
    ))}
  </CenteredRow>
);

const Intro = () => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [pendingStep, setPendingStep] = useState(null);
  const navigate = useNavigate();
  const client = useQueryClient();
  const [cookies] = useCookies([]);
  const [bday, setBday] = useState("");
  const [level, setLevel] = useState(0);
  const [gender, setGender] = useState(1);
  const { mobileDevice } = useScreenSize();
  const [boxValue, setBoxValue] = useState("");
  const [timezone, setTimezone] = useState(``);
  const [countryId, setCountryId] = useState(``);
  const [weightValue, setWeightValue] = useState("");
  const [heightValue, setHeightValue] = useState("");
  const { enableNotification } = useNotifications();
  const [bDayError, setBDayError] = useState(false);
  const [weightError, setWeightError] = useState(false);
  const [heightError, setHeightError] = useState(false);
  const [countryError, setCountryError] = useState(false);
  const [activeRequest, setActiveRequest] = useState(false);
  const [bDayErrorText, setBDayErrorText] = useState(false);
  const [weightErrorText, setWeightErrorText] = useState(false);
  const [heightErrorText, setHeightErrorText] = useState(false);
  const [countryErrorText, setCountryErrorText] = useState(false);

  useEffect(() => {
    getUsersTimeZone();
  }, []);

  useEffect(() => {
    axios.defaults.headers.common["X-CSRFToken"] = cookies.csrftoken;
  }, [cookies]);

  const { data: countries, isPending: countriesPending } = useQuery({
    queryKey: ["countries"],
    queryFn: ({ signal }) => getCountries({ signal }),
    staleTime: 12 * 60 * 60 * 1000,
  });

  const updateAthleteMutation = useMutation({
    mutationFn: completeProfile,
    onSuccess: () => {
      setActiveRequest(false);
      client.invalidateQueries(["profile", user.id]);
      client.invalidateQueries(["user", user.id]);
      setTimeout(() => navigate(routes.PROFILE), 500);
    },
    onError: (error) => {
      setActiveRequest(false);
      if (error.response.status === 400) {
        enableNotification(error.response.data.message, "error");
        return;
      }

      if (error.response.status === 403) {
        navigate(routes.LOGIN);
        return;
      }

      if (error.response.status === 500)
        enableNotification(errorTexts.system_error, "error");
    },
  });

  let getUsersTimeZone = () => {
    let hoursOffset = new Date().getHours() - new Date().getUTCHours();

    if (hoursOffset !== 0 && hoursOffset !== null) {
      hoursOffset = new Intl.NumberFormat("en-US", {
        signDisplay: "exceptZero",
      }).format(hoursOffset);
    } else {
      hoursOffset = "";
    }
    var usersTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTimezone(usersTimezone);
  };

  // Requests a step change without touching `step` directly: this flips the
  // current step's `in` prop to false so it plays its exit animation, and only
  // once that finishes (handleStepExited) does `step` actually advance, which
  // is what lets the next step start its enter animation. This keeps the two
  // transitions sequential instead of both running at once.
  let requestStep = (n) => {
    if (pendingStep !== null) return;
    setPendingStep(n);
  };

  let handleStepExited = () => {
    setPendingStep((p) => {
      if (p !== null) setStep(p);
      return null;
    });
  };

  // Step 1 -> 2: identity basics
  let goToStep2 = () => {
    setCountryError(false);
    setBDayError(false);

    const age = dayjs().subtract(16, "year");

    if (countryId === ``) {
      setCountryError(true);
      setCountryErrorText("Country is required.");
      return;
    }
    if (isEmpty(bday)) {
      setBDayError(true);
      setBDayErrorText("Birthday is required.");
      return;
    }
    if (dayjs(bday).isAfter(age)) {
      setBDayError(true);
      setBDayErrorText(
        "You have to be at least 16 years old to create an account.",
      );
      return;
    }

    requestStep(2);
  };

  // Step 2 -> 3: body stats
  let goToStep3 = () => {
    setWeightError(false);
    setHeightError(false);

    if (isEmpty(weightValue)) {
      setWeightError(true);
      setWeightErrorText("Weight is required.");
      return;
    }
    if (parseFloat(weightValue) > 250) {
      setWeightError(true);
      setWeightErrorText("Invalid weight. Maximum is 250kg.");
      return;
    }
    if (parseFloat(weightValue) < 0) {
      setWeightError(true);
      setWeightErrorText("Weight can not be a negative number.");
      return;
    }
    if (isEmpty(heightValue)) {
      setHeightError(true);
      setHeightErrorText("Height is required.");
      return;
    }
    if (parseInt(heightValue) < 100) {
      setHeightError(true);
      setHeightErrorText("Invalid height. Minimum is 100cm.");
      return;
    }

    requestStep(3);
  };

  let completeUserProfile = async () => {
    setActiveRequest(true);
    updateAthleteMutation.mutate({
      countryId,
      level,
      user_weight: weightValue,
      user_height: heightValue,
      user_box: boxValue,
      bday,
      gender,
      timezone,
    });
  };

  if (user.setup === 1) return <Navigate to={routes.PROFILE} />;

  return (
    <AllCenteredColumn
      height={"100vh"}
      sx={{ background: Theme.palette.background }}
      overflow={"hidden"}
    >
      {/* Pinned above the steps (not inside any Slide) so it stays put while
          the step content underneath animates in/out. */}
      <Stack alignItems={"center"} sx={{ mb: 3 }}>
        <GPLogo size={72} />
      </Stack>
      {/* Each step transitions sequentially: requestStep() only flips the
          current step's `in` to false, so it plays its exit animation while
          the next step stays unmounted; handleStepExited then advances `step`,
          which is what lets the next step mount and play its enter animation.
          Since only one step is ever mounted at a time, they can sit in normal
          flow instead of needing an absolute-position overlay. */}
      <Stack
        gap={3}
        minWidth={!mobileDevice && "385px"}
        width={mobileDevice ? "95%" : "30%"}
      >
        <Stack gap={1} alignItems={"center"} textAlign={"center"}>
          <SecondaryTitle>
            Step {step} of {TOTAL_STEPS}
          </SecondaryTitle>
          <MainTitle>
            {step === 1
              ? "Who are you?"
              : step === 2
                ? "Your Body"
                : "Training Level"}
          </MainTitle>
          <MainText
            fontSize={"0.85rem"}
            sx={{ color: Theme.palette.textSub + " !important" }}
          >
            {step === 1
              ? "Tell us about yourself to personalise your experience."
              : step === 2
                ? "We use this to calculate your performance metrics."
                : "Choose your fitness level. You can change this later."}
          </MainText>
          <Box sx={{ pt: 1 }}>
            <StepIndicator step={step} />
          </Box>
        </Stack>
        <Slide
          unmountOnExit
          in={step === 1 && pendingStep === null}
          direction="left"
          timeout={STEP_TRANSITION_MS}
          onExited={handleStepExited}
        >
          <Stack gap={2}>
            <Box
              sx={{
                position: "relative",
                background: Theme.palette.card,
                border: `1px solid ${Theme.palette.borderLight}`,
                borderRadius: "20px",
                padding: mobileDevice ? "20px 18px" : "28px",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "3px",
                  background: "linear-gradient(90deg, #f97316, #ea580c)",
                },
              }}
            >
              <Stack gap={3}>
                <TextInput
                  disabled={activeRequest}
                  label={"Box / Gym"}
                  placeholder={"Enter your box / gym"}
                  val={boxValue}
                  onChange={(e) => setBoxValue(e.target.value)}
                  icon={"🏠"}
                />
                <Stack gap={0.5}>
                  {!countriesPending && (
                    <SelectInput
                      options={countries}
                      label={"Country *"}
                      placeholder={"Select your country"}
                      setValue={setCountryId}
                      value={countryId}
                      icon={"🌐"}
                    />
                  )}
                  {countryError && <ErrorText>{countryErrorText}</ErrorText>}
                </Stack>
                <Stack gap={0.5}>
                  <DateInput label={"Birthday *"} setValue={setBday} />
                  {bDayError && <ErrorText>{bDayErrorText}</ErrorText>}
                </Stack>
              </Stack>
            </Box>
            <FormButton onClick={goToStep2}>Continue →</FormButton>
          </Stack>
        </Slide>
        <Slide
          unmountOnExit
          in={step === 2 && pendingStep === null}
          direction="left"
          timeout={STEP_TRANSITION_MS}
          onExited={handleStepExited}
        >
          <Stack gap={2}>
            <Box
              sx={{
                position: "relative",
                background: Theme.palette.card,
                border: `1px solid ${Theme.palette.borderLight}`,
                borderRadius: "20px",
                padding: mobileDevice ? "20px 18px" : "28px",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "3px",
                  background: "linear-gradient(90deg, #f97316, #ea580c)",
                },
              }}
            >
              <Stack gap={3}>
                <Stack width={"100%"} gap={0.5}>
                  <TextInput
                    label={"Weight (kg) *"}
                    val={weightValue}
                    onChange={(e) => setWeightValue(e.target.value)}
                    type={"number"}
                    step={0.1}
                    disabled={activeRequest}
                  />
                  {weightError && <ErrorText>{weightErrorText}</ErrorText>}
                </Stack>
                <Stack width={"100%"} gap={0.5}>
                  <TextInput
                    label={"Height (cm) *"}
                    val={heightValue}
                    onChange={(e) => setHeightValue(e.target.value)}
                    disabled={activeRequest}
                    type={"number"}
                    step={1}
                  />
                  {heightError && <ErrorText>{heightErrorText}</ErrorText>}
                </Stack>
                {/* <RadioInput
                  item={"Gender *"}
                  setValue={setGender}
                  value={gender}
                  options={genderOptions}
                /> */}
                <Stack gap={1} alignItems={"flex-start"}>
                  <SecondaryTitle>Gender</SecondaryTitle>
                  <ToggleButton
                    items={genderOptions}
                    setId={setGender}
                    id={gender}
                  />
                </Stack>
              </Stack>
            </Box>
            <Stack gap={2} direction={"row"}>
              <Box flex={1}>
                <FormButton
                  alt={true}
                  small={true}
                  onClick={() => requestStep(1)}
                >
                  Back
                </FormButton>
              </Box>
              <Box flex={1}>
                <FormButton small={true} onClick={goToStep3}>
                  Next
                </FormButton>
              </Box>
            </Stack>
          </Stack>
        </Slide>
        <Slide
          unmountOnExit
          in={step === 3 && pendingStep === null}
          direction="left"
          timeout={STEP_TRANSITION_MS}
          onExited={handleStepExited}
        >
          <Stack gap={2}>
            <Box
              sx={{
                position: "relative",
                background: Theme.palette.card,
                border: `1px solid ${Theme.palette.borderLight}`,
                borderRadius: "20px",
                padding: mobileDevice ? "20px 18px" : "28px",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "3px",
                  background: "linear-gradient(90deg, #f97316, #ea580c)",
                },
              }}
            >
              <Stack gap={3}>
                <Stack gap={1}>
                  {LEVELOPTIONS.map((item, index) => (
                    <Stack
                      key={index}
                      onClick={() => setLevel(item.id)}
                      p={1}
                      sx={{
                        border: 1,
                        borderRadius: 3,
                        background: level === item.id && item.color + "07",
                        borderColor:
                          level === item.id ? item.color : Theme.palette.muted,
                      }}
                    >
                      <MainText
                        fontWeight={600}
                        sx={{
                          color:
                            level === item.id
                              ? item.color + " !important"
                              : Theme.palette.text + " !important",
                        }}
                      >
                        {item.name}
                      </MainText>
                      <MainText
                        sx={{ color: Theme.palette.muted + " !important" }}
                      >
                        {item.desc}
                      </MainText>
                    </Stack>
                  ))}
                  <CenteredRow gap={1} mt={1}>
                    <MainText fontSize={"0.8rem"}>
                      Not sure which level to choose? See the level guide here.
                    </MainText>
                    <Suspense>
                      <LevelGuideModal />
                    </Suspense>
                  </CenteredRow>
                </Stack>
              </Stack>
            </Box>
            <SpacedRow gap={2}>
              <Box flex={1}>
                <FormButton
                  alt={true}
                  small={true}
                  disabled={activeRequest}
                  onClick={() => requestStep(2)}
                >
                  Back
                </FormButton>
              </Box>
              <Box flex={1}>
                <FormButton
                  small={true}
                  disabled={activeRequest}
                  onClick={completeUserProfile}
                >
                  Complete setup
                </FormButton>
              </Box>
            </SpacedRow>
          </Stack>
        </Slide>
      </Stack>
    </AllCenteredColumn>
  );
};
export default Intro;
